import "./utility-tools.css";
import { bmpCanvas, canvasBlob, decodeBmp, hasBmpHeader } from "./bmp-browser.js";
import { createOperationResult } from "./operation-result.js";
import { safeBaseName } from "./utility-tools-core.js";

const root = document.querySelector("[data-bmp-to-jpg]");

if (root) {
  const ids = ["bmpStatus", "bmpUploadPanel", "bmpDropZone", "chooseBmp", "bmpFileInput", "bmpWorkspace", "bmpPreview", "bmpFileName", "bmpQuality", "bmpQualityValue", "convertBmp", "replaceBmp"];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = { file: null, decoded: null, busy: false };
  const result = createOperationResult(root, elements.bmpWorkspace);

  function status(message = "") { elements.bmpStatus.textContent = message; elements.bmpStatus.hidden = !message; }
  function controls() { elements.convertBmp.disabled = state.busy || !state.decoded; elements.replaceBmp.disabled = state.busy; elements.bmpQuality.disabled = state.busy; }
  function validate(file, bytes) {
    if (file.size > 25 * 1024 * 1024) throw new Error("BMP files are limited to 25 MB.");
    if (!hasBmpHeader(bytes)) throw new Error("File content is not a valid BMP image.");
  }

  async function load(file) {
    if (!file || state.busy) return;
    status();
    state.busy = true;
    controls();
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      validate(file, bytes);
      const decoded = decodeBmp(bytes);
      state.file = file;
      state.decoded = decoded;
      const canvas = elements.bmpPreview;
      canvas.width = decoded.width;
      canvas.height = decoded.height;
      canvas.getContext("2d", { alpha: false }).putImageData(new ImageData(decoded.rgba, decoded.width, decoded.height), 0, 0);
      elements.bmpFileName.textContent = `${file.name} · ${decoded.width} × ${decoded.height} px`;
      elements.bmpUploadPanel.hidden = true;
      elements.bmpWorkspace.hidden = false;
    } catch (error) {
      state.file = null;
      state.decoded = null;
      status(error?.message || "This BMP image could not be opened.");
    } finally {
      state.busy = false;
      elements.bmpFileInput.value = "";
      controls();
    }
  }

  async function convert() {
    if (!state.decoded || state.busy) return;
    status();
    state.busy = true;
    controls();
    try {
      const blob = await canvasBlob(bmpCanvas(state.decoded), "image/jpeg", Number(elements.bmpQuality.value) / 100);
      result.show({
        blob,
        fileName: `${safeBaseName(state.file?.name, "bitmap")}.jpg`,
        message: "Your BMP image was converted successfully.",
        details: [`${state.decoded.width} × ${state.decoded.height} pixels`, `${elements.bmpQuality.value}% JPEG quality`]
      });
    } catch (error) {
      status(error?.message || "The JPEG could not be created.");
    } finally {
      state.busy = false;
      controls();
    }
  }

  const choose = () => elements.bmpFileInput.click();
  elements.chooseBmp.addEventListener("click", event => { event.stopPropagation(); choose(); });
  elements.replaceBmp.addEventListener("click", choose);
  elements.bmpFileInput.addEventListener("change", () => load(elements.bmpFileInput.files?.[0]));
  elements.convertBmp.addEventListener("click", convert);
  elements.bmpQuality.addEventListener("input", () => { elements.bmpQualityValue.textContent = `${elements.bmpQuality.value}%`; });
  elements.bmpDropZone.addEventListener("click", event => { if (!event.target.closest("button")) choose(); });
  elements.bmpDropZone.addEventListener("keydown", event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); choose(); } });
  elements.bmpDropZone.addEventListener("dragover", event => { event.preventDefault(); elements.bmpDropZone.classList.add("is-dragging"); });
  elements.bmpDropZone.addEventListener("dragleave", () => elements.bmpDropZone.classList.remove("is-dragging"));
  elements.bmpDropZone.addEventListener("drop", event => { event.preventDefault(); elements.bmpDropZone.classList.remove("is-dragging"); load(event.dataTransfer.files?.[0]); });
}
